import { useSessionUser, useUserCatalog } from "hooks/useSession";
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import {
  CURRENCIES,
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
} from "config";
import { BudgetCategory, BudgetContingency, BudgetI } from "./types";
import { resolveEffectiveBudgetFeatureStatus } from "./budgetAiRunState";
import { toBudgetDetail } from "./budgetDetailFromApi";
import {
  getProjectFeatureStatus,
  handleToast,
  mapProjectFeatureStatusesToFeatureStatus,
} from "utils";
import { useSearchParams } from "react-router-dom";
import { DetailedLevelBudgetGrid } from "./DetailedLevelBudgetGrid";
import { PieChart } from "lucide-react";
import { StepGenerateEmptyState, useStepGeneration } from "components/common/StepEmptyState";
import { TopSheetBudgetGrid } from "./TopSheetBudgetGrid";
import { BudgetBreakdown } from "./BudgetBreakdown";
import { BudgetHealthHeader, buildBudgetHealthMetrics } from "./BudgetHealthHeader";
import {
  DEFAULT_TAX_INCENTIVE_ASSUMPTIONS,
  TaxIncentiveAssumptions,
} from "./taxIncentiveEffectiveRate.types";
import { resolveNettingTaxIncentives } from "./budgetTaxIncentiveNetting";
import { useBudgetDisplayLocation } from "hooks/useBudget/useBudgetDisplayLocation";
import { useTaxIncentiveCatalog } from "hooks/useBudget/useTaxIncentiveCatalog";
import { TargetedRebudgetBanner } from "./TargetedRebudgetBanner";
import { BudgetRerunDiffModal } from "./BudgetRerunDiffModal";
import {
  buildBudgetRerunDiff,
  clearBudgetRerunSnapshot,
  readBudgetRerunSnapshot,
  saveBudgetRerunSnapshot,
} from "./budgetRerunSnapshot";
import { targetedRerunRefusalMessage } from "./targetedRerun";
import { useRebudgetGate } from "./useRebudgetGate";
import { budgetRerunCostLine, getBudgetRerunAvailability } from "utils/aiCreditsAvailability";
import { CategoryFringesGrid } from "./CategoryFringesGrid";
import { BudgetActions } from "./BudgetActions";
import { TaxIncentiveDialog } from "./EffectiveRateCard/TaxIncentiveDialog";
import { ProjectProductionBaseChip } from "./ProductionBaseChip";
import { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace";
import { resolveWorkspaceBudgetBenchmarksEnabled } from "hooks/useWorkspace/resolveWorkspaceBudgetBenchmarksEnabled";
import { status, SubscriptionType } from "store/types/enum";
import { AiPipelineRunningPanel } from "@/components/common/AiPipelineRunning";
import { BudgetLoader } from "./BudgetLoader";
import { PageLayout } from "components/layout/template/PageLayout";
import { PageHeader } from "components/layout/page/PageHeader";

/** Matches the sidebar label, so the title names the page the user picked. */
const BUDGET_PAGE_TITLE = "Production Budget";
import { QueryLoadError } from "components/common/QueryLoadError";
import {
  useBudgetApi,
  useBudgetMutations,
  useBudgetBenchmarks,
  useBudgetBenchmarkMeta,
} from "hooks/useBudget";
import { shouldShowScheduleBudgetTagsEducation } from "utils/scheduleBudgetTaggingCopy";
import { ScheduleBudgetTagsEducationBanner } from "components/common/ScheduleBudgetTagsEducationBanner";
import { useTagDefinitionsReport } from "features/script-editor/hooks/use-tag-definitions-report";
import { usePanelViewedOnce } from "hooks/usePanelViewedOnce";
import { ANALYTICS_EVENTS } from "analytics";
import type { BenchmarkSource } from "./budgetBenchmarks";
import { buildBudgetBenchmarkComparison } from "./buildBudgetBenchmarkComparison";
import { assessBenchmarkClassification } from "./budgetBucketClassification";
import { resolveBudgetBenchmarkMeta } from "./budgetBenchmarkDetails";
import {
  readStoredBudgetTopSheetViewMode,
  type BudgetTopSheetViewMode,
} from "./budgetTopSheetViewMode";
import { useUserPreferences } from "providers/user-preferences";
import { BudgetAiSyncContext } from "./BudgetAiSyncProvider";
import { useBudgetRunActive } from "hooks/budget/useBudgetRunActive";

export const subCategorySearchParamName = "subCategory";
export const budgetItemSearchParamName = "item";
export const categoryFringesSearchParamName = "categoryFringes";

/** Local budget state stamped with the project and draft it was loaded for. */
type DraftScoped<T> = { scope: string; value: T };

/** The stale-budget notice's category chooser: whether it is open, the picks and a refusal. */
type TargetedChooserState = { open: boolean; categoryIds: number[]; refusal: string | null };
const CLOSED_CHOOSER: TargetedChooserState = { open: false, categoryIds: [], refusal: null };

export const Budget = () => {
  const {
    preferences: userPreferences,
    patchPreferences,
    isHydrated: arePreferencesHydrated,
  } = useUserPreferences();
  const hydratedBudgetUserRef = useRef<string | null>(null);
  const [scopedSheetData, setScopedSheetData] = useState<DraftScoped<BudgetCategory[]>>();
  const [scopedBudgetDetail, setScopedBudgetDetail] =
    useState<DraftScoped<Omit<BudgetI, "categories"> | undefined>>();
  const [topSheetGridApi, setTopSheetGridApi] = useState<GridApi>();
  const gridRef = useRef<AgGridReact>(null);
  const { session, isOneTimePurchase } = useSessionUser();
  const { data: catalog } = useUserCatalog();
  const profile = session
    ? { ...session, workspaces: catalog?.workspaces ?? [], isOneTimePurchase }
    : null;
  const userId = useSelector((state: AppState) => state.auth.user_id);
  const { workspaceId } = useWorkspaceIdentifiers();

  const workspaceBudgetBenchmarksEnabled = useMemo(
    () => resolveWorkspaceBudgetBenchmarksEnabled(workspaceId, profile?.workspaces),
    [profile?.workspaces, workspaceId]
  );

  const budgetApi = useBudgetApi(true);
  const [rerunDiffOpen, setRerunDiffOpen] = useState(false);
  const [rerunDiffLines, setRerunDiffLines] = useState<ReturnType<typeof buildBudgetRerunDiff>>([]);
  const [rerunDiffTotals, setRerunDiffTotals] = useState({ before: 0, after: 0 });
  const [taxAssumptions, setTaxAssumptions] = useState<TaxIncentiveAssumptions>(
    DEFAULT_TAX_INCENTIVE_ASSUMPTIONS
  );
  const [selectedBenchmarkSource, setSelectedBenchmarkSource] =
    useState<BenchmarkSource>("industry");
  const pendingRerunDiffRef = useRef(false);

  const {
    project: initialProject,
    projectId,
    documentId,
    hasValidIds,
    isLoading: isProjectLoading,
  } = useProjectIdentifiers();
  // The budget query is keyed by project and draft. Another draft's budget, kept in local state
  // while the next one loads or fails, is never shown or edited as the current one.
  const budgetScope = `${projectId ?? ""}:${documentId ?? ""}`;
  const sheetData = scopedSheetData?.scope === budgetScope ? scopedSheetData.value : undefined;
  const budgetDetail =
    scopedBudgetDetail?.scope === budgetScope ? scopedBudgetDetail.value : undefined;

  const benchmarksQueryEnabled = Boolean(
    workspaceBudgetBenchmarksEnabled && sheetData && budgetDetail
  );
  const { isBudgetAiRunning, budgetProject } = useContext(BudgetAiSyncContext);
  const budgetGeneration = useStepGeneration("budget");
  // Same check the re-run guard and the assumptions dialog use.
  const isBudgetRunActive = useBudgetRunActive(projectId ?? "", documentId);
  const displayProjectForCredits = budgetProject ?? initialProject;
  const currentBudgetFeature = getProjectFeatureStatus(
    mapProjectFeatureStatusesToFeatureStatus(
      displayProjectForCredits?.featureStatuses,
      displayProjectForCredits?.projectId ?? projectId ?? 0
    ),
    "budget"
  );

  const {
    reRunBudgeting,
    createBudgetItem,
    deleteBudgetItem,
    duplicateBudgetItem,
    reorderBudgetItems,
    setBudgetLock,
  } = useBudgetMutations({
    projectId: projectId ?? "",
    documentId: documentId ?? "",
    budgetId: budgetApi.budgetId,
  });

  usePanelViewedOnce(
    ANALYTICS_EVENTS.BUDGET_PANEL_VIEWED,
    hasValidIds && projectId && documentId ? `${projectId}:${documentId}` : null,
    { project_id: String(projectId), document_id: String(documentId) }
  );

  const budgetBenchmarks = useBudgetBenchmarks({
    productionType: displayProjectForCredits?.productionType,
    enabled: benchmarksQueryEnabled,
    selectedSource: selectedBenchmarkSource,
  });
  const { meta: benchmarkMeta } = useBudgetBenchmarkMeta(benchmarksQueryEnabled);
  // Industry whenever peers are unavailable, even if the user picked peers.
  const { benchmarkSource, activeBenchmarkSet } = budgetBenchmarks;

  const [searchParams, setSearchParams] = useSearchParams();
  const [subCategoryId, setSubCategoryId] = useState<number | null>();
  const [itemId, setItemId] = useState<number | null>();
  const [categoryId, setCategoryId] = useState<number | null>();

  const {
    budgetData,
    budgetId,
    refetchBudget,
    updateBudgetInfo,
    updateBudget,
    updateBudgetCurrency,
  } = budgetApi;

  const budgetLocked = budgetData?.locked ?? budgetDetail?.locked ?? false;

  const previousStatusRef = useRef<string | undefined>(undefined);
  const isInitialMountRef = useRef(true);
  const completedRefetchHandledRef = useRef(false);

  // Track initial mount and previous status to avoid showing toast on initial load
  useEffect(() => {
    if (isInitialMountRef.current) {
      previousStatusRef.current = resolveEffectiveBudgetFeatureStatus(
        currentBudgetFeature?.status,
        currentBudgetFeature?.metadata
      );
      isInitialMountRef.current = false;
      return;
    }
  }, []);

  // Handle completion/failure side effects
  // Polling stops automatically when status changes from an in-flight state
  useEffect(() => {
    const featureStatus = resolveEffectiveBudgetFeatureStatus(
      currentBudgetFeature?.status,
      currentBudgetFeature?.metadata
    );
    const previousStatus = previousStatusRef.current;

    // Only react when status transitioned out of an in-flight state (RERUNNING or
    // initial PENDING generation). This prevents showing a toast on initial load when
    // status is already COMPLETED.
    const wasRerunning = previousStatus === FEATURE_STATUS_RERUNNING;
    const wasInFlight = wasRerunning || previousStatus === FEATURE_STATUS_PENDING;

    if (wasInFlight) {
      if (featureStatus === FEATURE_STATUS_COMPLETED) {
        // Toast + cache invalidation for all budget surfaces live in BudgetAiSyncProvider.
        if (wasRerunning && projectId && documentId) {
          pendingRerunDiffRef.current = true;
        }
      }
    }

    // Update previous status for next comparison
    previousStatusRef.current = resolveEffectiveBudgetFeatureStatus(
      currentBudgetFeature?.status,
      currentBudgetFeature?.metadata
    );

    if (
      featureStatus === FEATURE_STATUS_RERUNNING &&
      previousStatus !== FEATURE_STATUS_RERUNNING &&
      sheetData &&
      budgetDetail &&
      projectId &&
      documentId
    ) {
      saveBudgetRerunSnapshot(projectId, documentId, sheetData, {
        contingency_percentage: budgetDetail.contingency_percentage,
        locked: budgetDetail.contingency_locked,
        actual_amount: budgetDetail.contingency_actual,
      });
    }
  }, [
    currentBudgetFeature?.status,
    currentBudgetFeature?.metadata,
    projectId,
    documentId,
    sheetData,
    budgetDetail,
  ]);

  // Safety net: if the run completed but the cached budget query is still empty (e.g. the
  // tab was backgrounded while polling paused, or it cached a null result during the
  // PENDING phase), refetch once to load the freshly generated budget.
  useEffect(() => {
    const effectiveStatus = resolveEffectiveBudgetFeatureStatus(
      currentBudgetFeature?.status,
      currentBudgetFeature?.metadata
    );
    if (effectiveStatus !== FEATURE_STATUS_COMPLETED) {
      completedRefetchHandledRef.current = false;
      return;
    }
    if (!budgetData && !completedRefetchHandledRef.current) {
      completedRefetchHandledRef.current = true;
      refetchBudget();
    }
  }, [currentBudgetFeature?.status, currentBudgetFeature?.metadata, budgetData, refetchBudget]);

  useEffect(() => {
    const budgetSubCategoryId = Number(searchParams.get(subCategorySearchParamName));
    const budgetItemId = Number(searchParams.get(budgetItemSearchParamName));
    const categoryId = Number(searchParams.get(categoryFringesSearchParamName));
    setSubCategoryId(budgetSubCategoryId ?? null);
    setItemId(budgetItemId ?? null);
    setCategoryId(categoryId ?? null);
  }, [searchParams]);

  const isTopSheetView = !subCategoryId && !itemId && !categoryId;

  const [topSheetViewMode, setTopSheetViewMode] = useState<BudgetTopSheetViewMode>(() =>
    readStoredBudgetTopSheetViewMode(userId)
  );

  useEffect(() => {
    if (!userId || !arePreferencesHydrated || hydratedBudgetUserRef.current === userId) {
      return;
    }

    hydratedBudgetUserRef.current = userId;
    const savedViewMode = userPreferences.budget?.topSheetViewMode;
    if (savedViewMode) {
      setTopSheetViewMode(savedViewMode);
      return;
    }

    patchPreferences({
      budget: { topSheetViewMode: readStoredBudgetTopSheetViewMode(userId) },
    });
  }, [arePreferencesHydrated, patchPreferences, userId, userPreferences.budget?.topSheetViewMode]);

  const handleTopSheetViewModeChange = useCallback(
    (nextViewMode: BudgetTopSheetViewMode) => {
      setTopSheetViewMode(nextViewMode);
      patchPreferences({ budget: { topSheetViewMode: nextViewMode } });
    },
    [patchPreferences]
  );

  const benchmarkComparison = useMemo(() => {
    if (!workspaceBudgetBenchmarksEnabled || !isTopSheetView || !sheetData || !budgetDetail) {
      return null;
    }
    return buildBudgetBenchmarkComparison(
      sheetData,
      budgetDetail.contingency_percentage,
      displayProjectForCredits?.productionType,
      activeBenchmarkSet,
      benchmarkSource
    );
  }, [
    activeBenchmarkSet,
    benchmarkSource,
    budgetDetail,
    displayProjectForCredits?.productionType,
    isTopSheetView,
    sheetData,
    workspaceBudgetBenchmarksEnabled,
  ]);

  const benchmarkClassification = useMemo(() => {
    if (!isTopSheetView || !sheetData?.length) return null;
    return assessBenchmarkClassification(sheetData);
  }, [isTopSheetView, sheetData]);

  // Update local state when budget data changes
  useEffect(() => {
    if (budgetData) {
      const categories = budgetData.categories;
      setScopedBudgetDetail({ scope: budgetScope, value: toBudgetDetail(budgetData) });
      setScopedSheetData((prev) =>
        prev?.scope === budgetScope && prev.value === categories
          ? prev
          : { scope: budgetScope, value: categories }
      );
    } else if (budgetData === null) {
      setScopedBudgetDetail({ scope: budgetScope, value: undefined });
      setScopedSheetData({ scope: budgetScope, value: [] });
    }
  }, [budgetData, budgetScope]);

  const handleUpdateBudgetCurrency = (currency: string) => {
    updateBudgetCurrency.mutate(currency);
  };

  const handleSetSheetData = useCallback(
    (newData: BudgetCategory[]) => {
      setScopedSheetData({ scope: budgetScope, value: newData });
    },
    [budgetScope]
  );

  const handleUpdateBudgetDetail = useCallback(
    (newDetail: Partial<BudgetI>) => {
      setScopedBudgetDetail((prev) =>
        prev?.scope === budgetScope && prev.value
          ? { scope: budgetScope, value: { ...prev.value, ...newDetail } }
          : prev
      );
    },
    [budgetScope]
  );

  const handleSetTopSheetGridApi = useCallback((gridApi: GridApi | undefined) => {
    setTopSheetGridApi(gridApi);
  }, []);

  const budgetContingency: BudgetContingency | null = budgetDetail
    ? {
        contingency_percentage: budgetDetail.contingency_percentage,
        locked: budgetDetail.contingency_locked,
        actual_amount: budgetDetail.contingency_actual,
      }
    : null;

  // Budget never ran and there are no line items: the step's empty state in
  // place of an empty grid. Templates and Import in the actions stay as the
  // manual ways in. A budget with categories shows as before.
  const showGenerateEmptyState =
    budgetGeneration.notGenerated &&
    !isBudgetAiRunning &&
    !budgetApi.isBudgetLoading &&
    !subCategoryId &&
    !itemId &&
    !categoryId &&
    !budgetData?.categories?.length;

  // Full-page loader for project/budget fetch only. AI PENDING/RERUNNING uses AiPipelineRunningPanel.
  // A loaded project without a screenplay never gets valid ids, so missing ids alone do not count.
  const shouldShowLoader = !isBudgetAiRunning && (isProjectLoading || budgetApi.isBudgetLoading);

  const currencySymbol =
    CURRENCIES.find((cur) => cur.abbreviation === (budgetDetail?.currency || "USD").toUpperCase())
      ?.symbol ?? "$";

  const displayLocation = useBudgetDisplayLocation(budgetDetail);
  const { isReady: taxCatalogReady } = useTaxIncentiveCatalog();
  const budgetHealthMetrics = useMemo(() => {
    if (!sheetData || !budgetContingency) return null;
    return buildBudgetHealthMetrics(
      sheetData,
      budgetContingency,
      budgetDetail?.estimated_shoot_days,
      resolveNettingTaxIncentives(
        budgetDetail?.tax_incentives,
        taxCatalogReady ? displayLocation.taxState : null,
        budgetDetail?.currency
      ),
      taxAssumptions
    );
  }, [
    sheetData,
    budgetContingency,
    budgetDetail?.estimated_shoot_days,
    budgetDetail?.tax_incentives,
    budgetDetail?.currency,
    displayLocation.taxState,
    taxCatalogReady,
    taxAssumptions,
  ]);

  const handleOverageClick = useCallback(
    (subCategoryId: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(subCategorySearchParamName, String(subCategoryId));
      newSearchParams.delete(budgetItemSearchParamName);
      newSearchParams.delete(categoryFringesSearchParamName);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (
      !pendingRerunDiffRef.current ||
      !sheetData ||
      !projectId ||
      !documentId ||
      !budgetContingency
    ) {
      return;
    }
    const snapshot = readBudgetRerunSnapshot(projectId, documentId);
    if (!snapshot) {
      pendingRerunDiffRef.current = false;
      return;
    }
    const diffLines = buildBudgetRerunDiff(snapshot.categories, sheetData);
    const afterTotal = buildBudgetHealthMetrics(
      sheetData,
      budgetContingency,
      budgetDetail?.estimated_shoot_days,
      budgetDetail?.tax_incentives
    ).grandTotal;
    setRerunDiffLines(diffLines);
    setRerunDiffTotals({ before: snapshot.grandTotal, after: afterTotal });
    setRerunDiffOpen(true);
    clearBudgetRerunSnapshot(projectId, documentId);
    pendingRerunDiffRef.current = false;
  }, [
    sheetData,
    projectId,
    documentId,
    budgetContingency,
    budgetDetail?.estimated_shoot_days,
    budgetDetail?.tax_incentives,
  ]);

  // Held here, not in the banner, so the chooser outlives the banner hiding while its request is
  // out and the refusal stays beside the picks that caused it. Scoped to the draft, so another
  // draft never inherits it.
  const [scopedChooser, setScopedChooser] = useState<DraftScoped<TargetedChooserState>>({
    scope: budgetScope,
    value: CLOSED_CHOOSER,
  });
  const chooser = scopedChooser.scope === budgetScope ? scopedChooser.value : CLOSED_CHOOSER;
  // Another draft starts closed; the old draft's chooser is dropped, not kept for a return.
  useEffect(() => {
    setScopedChooser((prev) =>
      prev.scope === budgetScope ? prev : { scope: budgetScope, value: CLOSED_CHOOSER }
    );
  }, [budgetScope]);
  /** Changes the chooser of `scope` only, so a late answer for another draft changes nothing. */
  const updateChooser = useCallback(
    (scope: string, patch: Partial<TargetedChooserState>) =>
      setScopedChooser((prev) =>
        prev.scope === scope ? { scope, value: { ...prev.value, ...patch } } : prev
      ),
    []
  );
  // The full rerun dialog opens from the header's Rebudget button and the banner's Update all.
  const [isFullRerunOpen, setIsFullRerunOpen] = useState(false);
  // Same gate as the header's Rebudget button, so the banner never offers what it refuses.
  const { block: rebudgetBlock, isViewer } = useRebudgetGate(budgetLocked);
  const handleTargetedSelectionChange = useCallback(
    // A refusal was about the old selection.
    (categoryIds: number[]) => updateChooser(budgetScope, { categoryIds, refusal: null }),
    [budgetScope, updateChooser]
  );
  const handleChooseCategoriesOpenChange = useCallback(
    // Closing forgets the picks and any refusal; opening starts from nothing picked.
    (open: boolean) => updateChooser(budgetScope, open ? { open } : CLOSED_CHOOSER),
    [budgetScope, updateChooser]
  );
  // Same availability the full rerun dialog reads, so both say the same thing about cost.
  const budgetRerunAvailability = getBudgetRerunAvailability(displayProjectForCredits);

  const handleTargetedRebudget = useCallback(
    (categoryIds: number[]) => {
      if (!budgetDetail?.rerun_params) {
        handleToast("Save budget assumptions before running a targeted rebudget.", status.ERROR);
        return;
      }
      const requestScope = budgetScope;
      updateChooser(requestScope, { refusal: null });
      const selected = new Set(categoryIds);
      const categoryNames = (sheetData ?? [])
        .filter((category) => selected.has(category.budget_category_id))
        .map((category) => category.name);
      reRunBudgeting.mutate(
        {
          ...budgetDetail.rerun_params,
          category_ids: categoryIds,
          category_names: categoryNames,
        },
        {
          onSuccess: () => updateChooser(requestScope, CLOSED_CHOOSER),
          onError: (error) => {
            const refusal = targetedRerunRefusalMessage(error);
            if (refusal) updateChooser(requestScope, { refusal });
          },
        }
      );
    },
    [budgetDetail?.rerun_params, budgetScope, reRunBudgeting, sheetData, updateChooser]
  );

  // The banner is gone, or the gate took its actions away. Unless this chooser's request is still
  // out, forget it: coming back must not reopen an old choice by itself. A refusal is the
  // exception: while the budget is still stale it stays, with its picks and the chooser open,
  // until the user has seen it and closed the chooser, or the draft changes. Once the budget is
  // current it goes too, so a later stale state never reopens it.
  const isBudgetStale = Boolean(displayProjectForCredits?.aiCredits?.staleness?.budget_stale);
  const isStaleBudgetNoticeShown = isBudgetStale && Boolean(sheetData) && !isBudgetAiRunning;
  const isChooserAvailable = isStaleBudgetNoticeShown && !rebudgetBlock;
  const isChooserDirty = chooser.open || chooser.categoryIds.length > 0 || chooser.refusal !== null;
  const shouldHoldRefusal = chooser.refusal !== null && isBudgetStale;
  useEffect(() => {
    if (isChooserAvailable || reRunBudgeting.isPending || !isChooserDirty) return;
    updateChooser(budgetScope, shouldHoldRefusal ? { open: true } : CLOSED_CHOOSER);
  }, [
    budgetScope,
    shouldHoldRefusal,
    isChooserAvailable,
    isChooserDirty,
    reRunBudgeting.isPending,
    updateChooser,
  ]);

  const budgetGridOrLoader =
    !subCategoryId && !itemId && !categoryId ? (
      <>
        <TopSheetBudgetGrid
          sheetData={sheetData}
          setSheetData={handleSetSheetData}
          updateBudgetDetail={handleUpdateBudgetDetail}
          budgetDetail={budgetDetail}
          updateBudget={updateBudget}
          topSheetGridApi={topSheetGridApi}
          setTopSheetGridApi={handleSetTopSheetGridApi}
          gridRef={gridRef}
          budgetId={budgetId}
          viewMode={topSheetViewMode}
        />
      </>
    ) : subCategoryId || itemId ? (
      <DetailedLevelBudgetGrid
        budgetItemId={itemId ?? null}
        subCategoryId={subCategoryId ?? null}
        budgetLocked={budgetLocked}
        sheetData={sheetData}
        updateBudgetInfo={updateBudgetInfo}
        createBudgetItem={createBudgetItem}
        deleteBudgetItemMutation={deleteBudgetItem}
        duplicateBudgetItem={duplicateBudgetItem}
        reorderBudgetItems={reorderBudgetItems}
        budgetId={budgetId}
      />
    ) : (
      categoryId && (
        <CategoryFringesGrid
          categoryId={categoryId}
          sheetData={sheetData}
          updateBudgetInfo={updateBudgetInfo}
        />
      )
    );

  const budgetAiOverlay = (
    <AiPipelineRunningPanel
      pipeline="budget"
      layout="panel-overlay"
      featureStatuses={displayProjectForCredits?.featureStatuses}
      projectId={displayProjectForCredits?.projectId ?? projectId ?? ""}
      hasExistingData={Boolean(budgetData)}
    />
  );
  const tagsReport = useTagDefinitionsReport(String(projectId), String(documentId), hasValidIds);
  const shouldShowTagsEducation = shouldShowScheduleBudgetTagsEducation({
    hasLoadedTagReport: tagsReport.isSuccess,
    rows: tagsReport.data,
    isTagReportFetching: tagsReport.isFetching,
    hasCompletedOutput:
      Boolean(budgetData) ||
      resolveEffectiveBudgetFeatureStatus(
        currentBudgetFeature?.status,
        currentBudgetFeature?.metadata
      ) === FEATURE_STATUS_COMPLETED,
  });

  // A failed load with nothing loaded for this draft is unknown data: no grid, totals or
  // actions to edit it. A failed background refresh keeps the budget already on screen.
  const showBudgetLoadError = budgetApi.isBudgetError && !sheetData;

  return shouldShowLoader ? (
    <BudgetLoader />
  ) : isBudgetAiRunning && !budgetData ? (
    <PageLayout>
      <div className="relative min-h-[calc(100svh-54px)] w-full max-w-auto">{budgetAiOverlay}</div>
    </PageLayout>
  ) : showBudgetLoadError ? (
    <PageLayout>
      <div className="flex min-w-0 w-full flex-col">
        <div className="flex-none py-6">
          <PageHeader title={BUDGET_PAGE_TITLE} />
        </div>
        <QueryLoadError
          message="Couldn't load this budget"
          onRetry={() => void refetchBudget()}
          isRetrying={budgetApi.isBudgetFetching}
        />
      </div>
    </PageLayout>
  ) : (
    <PageLayout>
      {/* A fixed-height column lets the grid fill the space left under the header. */}
      <div className="flex h-[calc(100svh-54px)] min-w-0 w-full flex-col overflow-x-hidden overflow-y-auto">
        <div className="flex-none py-6">
          <PageHeader
            title={BUDGET_PAGE_TITLE}
            titleAdornment={
              taxCatalogReady && budgetDetail ? (
                <ProjectProductionBaseChip
                  location={displayLocation}
                  budgetCurrency={budgetDetail.currency}
                  currencySymbol={currencySymbol}
                  budgetTotal={budgetHealthMetrics?.grandTotal ?? 0}
                  taxNetting={budgetHealthMetrics?.taxNetting ?? null}
                />
              ) : null
            }
            actions={
              <BudgetActions
                taxIncentiveAction={
                  isTopSheetView && budgetHealthMetrics?.taxNetting ? (
                    <TaxIncentiveDialog
                      summary={budgetHealthMetrics.taxNetting}
                      currencySymbol={currencySymbol}
                      assumptions={taxAssumptions}
                      onAssumptionsChange={setTaxAssumptions}
                    />
                  ) : undefined
                }
                sheetData={sheetData ?? []}
                budgetDetail={budgetDetail}
                budgetContingency={budgetContingency}
                gridRef={gridRef}
                currency={budgetDetail?.currency || "USD"}
                updateBudgetCurrency={handleUpdateBudgetCurrency}
                benchmarkComparison={benchmarkComparison}
                benchmarkClassification={benchmarkClassification}
                benchmarkSource={benchmarkSource}
                onBenchmarkSourceChange={setSelectedBenchmarkSource}
                peerSampleSize={budgetBenchmarks.peerSampleSize}
                peerBenchmarkAvailable={budgetBenchmarks.peerBenchmarkAvailable}
                benchmarkMeta={resolveBudgetBenchmarkMeta(benchmarkMeta)}
                activeBenchmarkSet={activeBenchmarkSet}
                budgetLocked={budgetLocked}
                onToggleBudgetLock={() => setBudgetLock.mutate({ locked: !budgetLocked })}
                isBudgetLockPending={setBudgetLock.isPending}
                isTopSheetView={isTopSheetView}
                topSheetViewMode={topSheetViewMode}
                onTopSheetViewModeChange={handleTopSheetViewModeChange}
                isEditAssumptionsOpen={isFullRerunOpen}
                onEditAssumptionsOpenChange={setIsFullRerunOpen}
              />
            }
          />
        </div>
        {isStaleBudgetNoticeShown && sheetData ? (
          <TargetedRebudgetBanner
            categories={sheetData}
            onRebudgetCategories={handleTargetedRebudget}
            onUpdateAll={() => setIsFullRerunOpen(true)}
            rerunBlock={rebudgetBlock}
            isViewer={isViewer}
            isPending={reRunBudgeting.isPending || isBudgetAiRunning || isBudgetRunActive}
            isSubmitting={reRunBudgeting.isPending}
            rerunParamsNotice={budgetDetail?.rerun_params_notice}
            costLine={budgetRerunCostLine(budgetRerunAvailability, {
              // Same rule as the full rerun dialog's footer.
              isFreePlan:
                !isOneTimePurchase && session?.pricing_plan?.plan_name === SubscriptionType.FREE,
            })}
            refusal={chooser.refusal}
            selectedCategoryIds={chooser.categoryIds}
            onSelectedCategoryIdsChange={handleTargetedSelectionChange}
            isChooseCategoriesOpen={chooser.open}
            onChooseCategoriesOpenChange={handleChooseCategoriesOpenChange}
          />
        ) : null}
        {shouldShowTagsEducation ? (
          <ScheduleBudgetTagsEducationBanner projectId={String(projectId)} />
        ) : null}
        {!subCategoryId && !itemId && !categoryId && budgetHealthMetrics ? (
          <BudgetHealthHeader
            metrics={budgetHealthMetrics}
            currencySymbol={currencySymbol}
            onOverageClick={handleOverageClick}
          />
        ) : null}
        {!subCategoryId && !itemId && !categoryId && sheetData && budgetDetail ? (
          <BudgetBreakdown
            categories={sheetData}
            contingencyPercentage={budgetDetail.contingency_percentage}
            currencySymbol={currencySymbol}
          />
        ) : null}
        <div className="flex min-h-[400px] min-w-0 flex-1 flex-col pb-8">
          {showGenerateEmptyState ? (
            <StepGenerateEmptyState stepId="budget" generation={budgetGeneration} icon={PieChart} />
          ) : (
            budgetGridOrLoader
          )}
        </div>
        {budgetAiOverlay}
        <BudgetRerunDiffModal
          open={rerunDiffOpen}
          onClose={() => setRerunDiffOpen(false)}
          diffLines={rerunDiffLines}
          currencySymbol={currencySymbol}
          beforeTotal={rerunDiffTotals.before}
          afterTotal={rerunDiffTotals.after}
        />
      </div>
    </PageLayout>
  );
};
